import { useCallback, useEffect, useState } from 'react';
import ShellLink from '../common/ShellLink';
import api from '../../services/api';
import { archivePath, canPublish, latestPath, parsePath } from './beatPublishPath';
import type { DeploymentPlan, NewsBeatDetail } from '../../types';

/**
 * Where a beat's briefings get published, if anywhere.
 *
 * OFF BY DEFAULT and it has to stay that way — a beat that quietly started
 * writing pages to a live site because a feature shipped would be a genuinely
 * bad surprise. Choosing a plan is the whole opt-in.
 *
 * The two URLs are previewed before saving, because they are the feature and
 * they are not guessable from a path field: one gains a date segment and the
 * other does not.
 */
export default function BeatPublishPanel({
  beat,
  onSaved,
}: {
  beat: NewsBeatDetail;
  onSaved: (beat: NewsBeatDetail) => void;
}) {
  const [plans, setPlans] = useState<DeploymentPlan[]>([]);
  const [planId, setPlanId] = useState<number | null>(beat.publish_plan_id ?? null);
  const [path, setPath] = useState((beat.publish_path || []).join('/'));
  const [archive, setArchive] = useState(beat.publish_archive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPlanId(beat.publish_plan_id ?? null);
    setPath((beat.publish_path || []).join('/'));
    setArchive(beat.publish_archive);
  }, [beat]);

  const loadPlans = useCallback(async () => {
    try {
      const res = await api.getDeploymentPlans({ page: 1, size: 100 });
      setPlans(res.items);
    } catch {
      // A failure here costs the dropdown, not the panel — the beat's current
      // setting still renders from what the beat itself carries.
      setPlans([]);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const segments = parsePath(path);
  const dirty =
    planId !== (beat.publish_plan_id ?? null) ||
    segments.join('/') !== (beat.publish_path || []).join('/') ||
    archive !== beat.publish_archive;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateNewsBeat(beat.id, {
        publish_plan_id: planId,
        publish_path: segments,
        publish_archive: archive,
      });
      onSaved(updated);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const sample = '2026-08-29';
  const publishes = canPublish({ planId, segments });

  return (
    <section className="pt-4 border-t border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
        Publish to a site
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
        Every completed run writes this beat's briefing to a site: one page that
        is always current, and a dated copy of each run beside it. Pages arrive
        marked <em>Ready to deploy</em>, so they go out on the next deploy - or
        wait in the review queue, if that site asks for review.
      </p>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Deployment plan
          </label>
          <select
            value={planId ?? ''}
            onChange={(e) => setPlanId(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800"
          >
            {/* The off switch, and it is first. */}
            <option value="">Don't publish this beat</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.managed_site_name ? `${p.managed_site_name} - ${p.name}` : p.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The plan carries the site and is what a deploy runs on.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Path on the site
          </label>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="briefings/oil"
            disabled={!planId}
            className="mt-1 block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Leave blank to publish at the site root.
          </p>
        </div>
      </div>

      {publishes && (
        <>
          <label className="mt-3 flex items-start gap-3 cursor-pointer max-w-2xl">
            <input
              type="checkbox"
              checked={archive}
              onChange={(e) => setArchive(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                Keep a dated copy of every run
              </span>
              <span className="block text-gray-500 dark:text-gray-400">
                This is what makes overwriting the current page safe. Without it,
                each run destroys what the last one said - and a link you emailed
                to &ldquo;this week&rsquo;s briefing&rdquo; becomes next
                week&rsquo;s.
              </span>
            </span>
          </label>

          {/* THE URLS, before saving. They are the feature, and one of them
              gains a date segment while the other does not — not something you
              can read off the path field. */}
          <div className="mt-3 text-sm border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-800/50 max-w-2xl">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Each run will write:
            </p>
            <p className="font-mono text-xs text-gray-800 dark:text-gray-200">
              {latestPath(segments)}{' '}
              <span className="font-sans text-gray-500 dark:text-gray-400">
 - always the latest briefing
              </span>
            </p>
            {archive && (
              <p className="font-mono text-xs text-gray-800 dark:text-gray-200 mt-0.5">
                {archivePath(segments, sample)}{' '}
                <span className="font-sans text-gray-500 dark:text-gray-400">
 - that run, kept
                </span>
              </p>
            )}
            {beat.publish_site_id && (
              <ShellLink
                to={`/deliver?site=${beat.publish_site_id}&tab=structure`}
                className="inline-block mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                See this site's structure ↗
              </ShellLink>
            )}
          </div>
        </>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
        >
          {saving ? 'Saving…' : 'Save publishing settings'}
        </button>
        <span className="text-sm text-gray-400 dark:text-gray-500">
          {saved ? (
            <span className="text-green-700 dark:text-green-400">Saved</span>
          ) : dirty ? (
            'Unsaved changes'
          ) : (
            'No changes yet'
          )}
        </span>
      </div>
    </section>
  );
}
